import { AfterViewInit, Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TagInformation} from '../../../model/tag-information';
import Handsontable from 'handsontable';
import {MatButton} from '@angular/material/button';


@Component({
    selector: 'app-tag-management-dialog',
    templateUrl: './tag-management-dialog.component.html',
    styleUrls: ['./tag-management-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton]
})
export class TagManagementDialogComponent implements AfterViewInit {
  private readonly dialogRef = inject<MatDialogRef<TagManagementDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);


  private tagMaxLength: number = 14;
  private riserMaxLength: number = 20;
  private floorMaxLength: number = 5;
  private roomMaxLength: number = 20;
  private buildingMaxLength: number = 20;
  private poLineMaxLength: number = 5;
  tags: TagInformation[];
  handsontable!: Handsontable;
  readonly errorMessage = signal<string>(undefined!);
  tableHeight: number;
  preventQuantityChanges: boolean;
  readonly canSave = signal<boolean>(true);

  constructor() {
    const tagDetails = this.data.tags;
    this.tableHeight = this.data.tableHeight || 640;
    if (this.data.quantity > tagDetails.length) {
      for (let index = tagDetails.length; index < this.data.quantity; index++) {
        const tag: TagInformation = new TagInformation();
        tag.sequence = index + 1;
        tagDetails.push(tag);
      }
    }
    this.tags = tagDetails;
    this.preventQuantityChanges = this.data.preventQuantityChanges || false;
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    this.errorMessage.set(undefined!);
    let valid: boolean = true;
    if ((this.tags?.length || 0) > 0) {
      for (const tag of this.tags) {
        if (!this.lengthIsValid(tag.tag, this.tagMaxLength)) {
          this.errorMessage.set(tag.tag + ' is not a valid tag value.  Max length is ' + this.tagMaxLength);
          valid = false;
        }
        if (!this.lengthIsValid(tag.riser, this.riserMaxLength)) {
          this.errorMessage.set(tag.riser + ' is not a valid riser vale.  Max length is ' + this.riserMaxLength);
          valid = false;
        }
        if (!this.lengthIsValid(tag.floor, this.floorMaxLength)) {
          this.errorMessage.set(tag.floor + ' is not a valid floor vale.  Max length is ' + this.floorMaxLength);
          valid = false;
        }
        if (!this.lengthIsValid(tag.room, this.roomMaxLength)) {
          this.errorMessage.set(tag.room + ' is not a valid room vale.  Max length is ' + this.roomMaxLength);
          valid = false;
        }
        if (!this.lengthIsValid(tag.building, this.buildingMaxLength)) {
          this.errorMessage.set(tag.building + ' is not a valid building vale.  Max length is ' + this.buildingMaxLength);
          valid = false;
        }
        if (!this.lengthIsValid(tag.poLine, this.poLineMaxLength)) {
          this.errorMessage.set(tag.poLine + ' is not a valid poLine vale.  Max length is ' + this.poLineMaxLength);
          valid = false;
        }
      }
    }
    if (valid) {
      this.dialogRef.close(this.tags);
    }
  }

  ngAfterViewInit(): void {
    const options = {
      data: this.tags,
      colHeaders: ['Tag', 'Riser/Loop', 'Floor', 'Room', 'Building', 'PO Line'],
      columns: [
        {
          data: 'tag',
          validator: (value: any, callback: any) => this.validateLength(value, callback, this.tagMaxLength, 'Tag'),
        },
        {
          data: 'riser',
          validator: (value: any, callback: any) => this.validateLength(value, callback, this.riserMaxLength, 'Riser/Loop'),
        },
        {
          data: 'floor',
          validator: (value: any, callback: any) => this.validateLength(value, callback, this.floorMaxLength, 'Floor'),
        },
        {
          data: 'room',
          validator: (value: any, callback: any) => this.validateLength(value, callback, this.roomMaxLength, 'Room'),
        },
        {
          data: 'building',
          validator: (value: any, callback: any) => this.validateLength(value, callback, this.buildingMaxLength, 'Building'),
        },
        {
          data: 'poLine',
          validator: (value: any, callback: any) => this.validateLength(value, callback, this.poLineMaxLength, 'PO Line'),
        },
      ],
      allowInsertRow: !this.preventQuantityChanges,
      allowRemoveRow: !this.preventQuantityChanges,
      contextMenu: this.preventQuantityChanges ? undefined : ['row_above', 'row_below', '---------', 'remove_row'],
      height: this.tableHeight,
      stretchH: 'all',
      allowInsertColumn: false,
      allowRemoveColumn: false,
      fillHandle: {
        direction: 'vertical',
        autoInsertRow: false
      }
    };
    const tagTable = document.getElementById('tagsTable');
    this.handsontable = new Handsontable(tagTable!, options);
    this.handsontable.addHook('beforeAutofill', [(start: any, end: any, data: any) => this.beforeAutofill(start, end, data)]);
    this.handsontable.addHook('beforeChange', [(changes: any, src: any) => this.beforeTagsChanged(changes, src)]);
    this.handsontable.addHook('beforeRemoveRow', [(index: any, amount: any) => this.beforeRowRemoved(index, amount)]);
  }

  beforeRowRemoved(index: number, amount: number): boolean {
    if (this.preventQuantityChanges) {
      return false;
    }
    if (this.handsontable.countRows() <= amount) {
      alert('Please delete the line instead of removing all tags');
      return false;
    }
    return true;
  }

  validateLength(value: string, callback: any, maxLength: number, fieldName: string): void {
    this.canSave.set(true);
    const isValid: boolean = this.lengthIsValid(value, maxLength);
    if (!isValid) {
      this.errorMessage.set(fieldName + ' can only be ' + maxLength + ' characters long');
      this.canSave.set(false);
    } else {
      this.errorMessage.set(null!);
    }
    callback(isValid);
  }

  private lengthIsValid(value: string, maxLength: number): boolean {
    return (value?.length || 0) <= maxLength;
  }

  beforeTagsChanged(changes: any, src: any): boolean {
    return src !== 'Autofill.fill';
  }

  beforeAutofill(start: any, end: any, data: any): boolean {
    const tagsGrid = this.handsontable;
    const startCol = start.col;
    const startRow = start.row;
    const endCol = end.col;
    const endRow = end.row;
    const intPattern = /\d+/g;
    let numberFound;
    let currentColumn = startCol;
    let currentRow = startRow;
    if (!data || data.length === 0) {
      return false;
    }
    const valueToCopy = data[0][0].toString();
    numberFound = valueToCopy.match(intPattern);
    if (numberFound && numberFound.length > 0) {
      const numberToReplace = numberFound[numberFound.length - 1];
      let replacementNumber = +numberToReplace;
      for (currentColumn = startCol; currentColumn <= endCol; currentColumn++) {
        for (currentRow = startRow; currentRow <= endRow; currentRow++) {
          tagsGrid.setDataAtCell(currentRow, currentColumn,
            valueToCopy.replace(numberToReplace, (++replacementNumber).toString()), 'autofill-override');
        }
      }
    } else {
      for (currentColumn = startCol; currentColumn <= endCol; currentColumn++) {
        for (currentRow = startRow; currentRow <= endRow; currentRow++) {
          tagsGrid.setDataAtCell(currentRow, currentColumn, valueToCopy, 'autofill-override');
        }
      }
    }
    return true;
  }

}
